import React, { useState, useEffect, useRef } from 'react'
import QuizContent from '../shared/QuizContent'
import LogoIcon from '../../assets/Logo.svg'
import { handleLoginFlow, getGoogleAuthUrl, collectAndStoreUTMData, handleAuthCallback,submitEmailOnly, getExternalClickIds, LP_SOURCE } from '../../utils/api'
import { useQuizSelections } from '../../hooks/useLocalStorage' // Add this import
import { useFormValidation, validationRules } from '../../hooks/useFormValidation'
import { useTranslation } from 'react-i18next'
import { useGBValue } from '../../hooks/useGBValue'
import { trackLoginCompleted } from '../../utils/analytics'
import OtpStep from './otpStep'

const Step15 = ({ onSwitchToSignup, onSwitchToForgot }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [otpState, setOtpState] = useState(null)
  const [selections] = useQuizSelections() // Get quiz selections
  const googleBtnRef = useRef(null)

  // Use the custom validation hook
  const { values, validateAll, getFieldProps } = useFormValidation(
    { email: '', password: '' },
    {
      email: validationRules.email,
      password: validationRules.password,
    },
  )

  const { t } = useTranslation()
  const ab_testing = useGBValue()
  const isMagicLink = ab_testing['createsexfriends-gray-magicLink'] === 'ON'

  useEffect(() => {
    const utmObj = collectAndStoreUTMData()
    let cancelled = false
    ;(async () => {
      const externalClickIds = await getExternalClickIds()
      if (cancelled || !googleBtnRef.current) return
      const finalState = {
        ...utmObj,
        lp_source: LP_SOURCE,
        externalClickIds,
      }
      const googleAuthUrl = getGoogleAuthUrl()
      googleBtnRef.current.href = `${googleAuthUrl}?state=${encodeURIComponent(JSON.stringify(finalState))}`
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    // Handle auth callback with selections
    handleAuthCallback(selections)
  }, [selections])

  const handleEmailOnlySubmit = async () => {
    const emailErr = validationRules.email(values.email)
    if (emailErr) {
      setError(emailErr)
      return
    }
    setIsLoading(true)
    setError('')
    try {
      const data = await submitEmailOnly(values.email, { selections, ab_testing })

      if (data?.success === false) {
        setError(data.message || t('please_check_email'))
        return
      }

      if (data?.otp_sent) {
        setOtpState({ email: values.email, otpExpiry: data.otpExpiry })
        return
      }

      if (data?.redirectUrl || data?.loginUrl) {
        trackLoginCompleted()
        localStorage.setItem('isCompleted', true)
        window.location.href = data.redirectUrl || data.loginUrl
        return
      }

      setError(data?.message || t('please_check_email'))
    } catch (err) {
      setError(err?.message || t('network_error'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoginSubmit = async e => {
    e.preventDefault()

    if (isMagicLink) {
      await handleEmailOnlySubmit()
      return
    }

    // Validate all fields
    if (!validateAll()) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      // Pass selections to login flow for model-params
      const result = await handleLoginFlow(values.email, values.password, selections)

      if (result.success) {
        trackLoginCompleted()
        localStorage.setItem('isCompleted', true)
        window.location.href = result.redirectUrl
      } else {
        setError(result.message || t('please_check_email'))
      }
    } catch (err) {
      setError(t('network_error'))
    } finally {
      setIsLoading(false)
    }
  }

  // Get CSS classes for input styling
  const getEmailInputClass = () => {
    const emailProps = getFieldProps('email')
    if (emailProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const getPasswordInputClass = () => {
    const passwordProps = getFieldProps('password')
    if (passwordProps.hasError) return 'form-input error-input'
    return 'form-input'
  }

  const handleOtpVerified = data => {
    trackLoginCompleted()
    localStorage.setItem('isCompleted', true)
    if (data?.redirectUrl || data?.loginUrl) {
      window.location.href = data.redirectUrl || data.loginUrl
    }
  }

  if (otpState) {
    return (
      <QuizContent className='account-page'>
        <OtpStep
          email={otpState.email}
          selections={selections}
          ab_testing={ab_testing}
          initialOtpExpiry={otpState.otpExpiry}
          onBack={() => setOtpState(null)}
          onVerified={handleOtpVerified}
        />
      </QuizContent>
    )
  }

  return (
    <QuizContent className='account-page'>
      <div className='account-container'>
        <div className='account-form-wrapper'>
          <div className='form-header'>
            <h2 className='account-title'>{t('log_in')}</h2>
            <p className='account-subtitle'>
              to <img src={LogoIcon} alt='' />
            </p>
            <button className='close-button'>×</button>
          </div>

          <form onSubmit={handleLoginSubmit} className='account-form'>
            <a ref={googleBtnRef} href='#' className='google-button'>
              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z'
                  fill='#4285F4'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M8.99976 18C11.4298 18 13.467 17.1941 14.9561 15.8195L12.0475 13.5614C11.2416 14.1014 10.2107 14.4204 8.99976 14.4204C6.65567 14.4204 4.67158 12.8373 3.96385 10.71H0.957031V13.0418C2.43794 15.9832 5.48158 18 8.99976 18Z'
                  fill='#34A853'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 8.99999C3.68182 8.40681 3.78409 7.82999 3.96409 7.28999V4.95818H0.957273C0.347727 6.17318 0 7.54772 0 8.99999C0 10.4523 0.347727 11.8268 0.957273 13.0418L3.96409 10.71Z'
                  fill='#FBBC05'
                />
                <path
                  fillRule='evenodd'
                  clipRule='evenodd'
                  d='M8.99976 3.57955C10.3211 3.57955 11.5075 4.03364 12.4402 4.92545L15.0216 2.34409C13.4629 0.891818 11.4257 0 8.99976 0C5.48158 0 2.43794 2.01682 0.957031 4.95818L3.96385 7.29C4.67158 5.16273 6.65567 3.57955 8.99976 3.57955Z'
                  fill='#EA4335'
                />
              </svg>{' '}
              {t('sign_in_google')}
            </a>

            <div className='separator'>
              <span>— {t('or')} —</span>
            </div>

            <div className='input-wrapper'>
              <input
                placeholder='Email'
                type='email'
                value={getFieldProps('email').value}
                onChange={getFieldProps('email').onChange}
                onBlur={getFieldProps('email').onBlur}
                className={getEmailInputClass()}
                aria-invalid={getFieldProps('email').hasError}
                aria-describedby={getFieldProps('email').hasError ? 'login-email-error' : undefined}
                autoComplete='email'
              />
              {getFieldProps('email').hasError && (
                <div id='login-email-error' className='error-message-input'>
                  {getFieldProps('email').error}
                </div>
              )}
            </div>
            {!isMagicLink && (
              <div className='cardStep15InputWrap'>
                <div className='input-wrapper'>
                  <input
                    placeholder='Password'
                    type={showPassword ? 'text' : 'password'}
                    value={getFieldProps('password').value}
                    onChange={getFieldProps('password').onChange}
                    onBlur={getFieldProps('password').onBlur}
                    className={getPasswordInputClass()}
                    required
                    minLength={6}
                    aria-invalid={getFieldProps('password').hasError}
                    aria-describedby={getFieldProps('password').hasError ? 'login-password-error' : undefined}
                    autoComplete='current-password'
                  />
                  <button
                    type='button'
                    className='cardStep15InputSvg'
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                        <path
                          d='M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    ) : (
                      <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' fill='none'>
                        <path
                          d='M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421'
                          stroke='#9B9CAA'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {getFieldProps('password').hasError && (
                  <div id='login-password-error' className='error-message-input'>
                    {getFieldProps('password').error}
                  </div>
                )}
              </div>
            )}

            {error && <div className='error-message'>{error}</div>}

            {!isMagicLink && (
              <div onClick={onSwitchToForgot} className='terms-text login'>
                <span>{t('forgot_password2')}</span>
              </div>
            )}

            <button type='submit' className='submit-button' disabled={isLoading}>
              {isLoading ? t('logging_in') : t('log_in')}
            </button>

            <div className='login-link'>
              {t('dont_have_account')}{' '}
              <button type='button' onClick={onSwitchToSignup} className='link-button'>
                {t('sign_up')} &gt;
              </button>
            </div>
          </form>
        </div>
      </div>
    </QuizContent>
  )
}

export default Step15
